/**
 * MC header search.
 *
 * A Search button (desktop and mobile) expands a full-width bar under the header row.
 * Opens with the button, Cmd/Ctrl + K, or "/" when not typing; Escape, the close button,
 * the scrim or focus leaving the bar closes it and returns focus to the button.
 *
 * Inside, the field is a combobox: ArrowUp/ArrowDown move a highlighted option
 * (aria-activedescendant) and Enter follows it. Results:
 * GET {predictive_search_url}?q=..&section_id=mc-search-results (server-rendered, so prices,
 * currency and member prices match the theme).
 */

const DEBOUNCE_MS = 180;
const SECTION_ID = 'mc-search-results';
const CLOSE_MS = 260;

class McSearchInline extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;
  /** @type {AbortController | null} */
  #request = null;
  /** @type {number | undefined} */
  #debounce;
  /** @type {number | undefined} */
  #hideTimer;
  /** @type {Map<string, string>} */
  #cache = new Map();
  #activeIndex = -1;
  #lastQuery = '';

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.toggle = /** @type {HTMLButtonElement} */ (this.querySelector('[data-mc-search-toggle]'));
    this.panel = /** @type {HTMLElement} */ (this.querySelector('[data-mc-search-panel]'));
    this.input = /** @type {HTMLInputElement} */ (this.querySelector('[data-mc-search-input]'));
    this.listbox = /** @type {HTMLElement} */ (this.querySelector('[data-mc-search-popover]'));
    this.popular = this.querySelector('[data-mc-search-popular]');
    this.results = /** @type {HTMLElement} */ (this.querySelector('[data-mc-search-results]'));
    this.kbd = this.querySelector('[data-mc-search-kbd]');
    this.clear = this.querySelector('[data-mc-search-clear]');
    this.status = this.querySelector('[data-mc-search-status]');
    if (!this.toggle || !this.panel || !this.input || !this.listbox || !this.results) return;

    const isMac = /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);
    if (this.kbd && !matchMedia('(pointer: coarse)').matches) {
      this.kbd.textContent = isMac ? '⌘K' : 'Ctrl K';
      this.kbd.hidden = false;
    }

    const narrow = window.matchMedia('(max-width: 749px)');
    const setPlaceholder = () => {
      const text = narrow.matches ? this.input.dataset.placeholderShort : this.input.dataset.placeholderFull;
      if (text) this.input.placeholder = text;
    };
    setPlaceholder();
    narrow.addEventListener('change', setPlaceholder, { signal });

    this.toggle.addEventListener('click', () => (this.isOpen ? this.close() : this.open()), { signal });
    this.querySelector('[data-mc-search-close]')?.addEventListener('click', () => this.close(), { signal });
    this.querySelector('[data-mc-search-scrim]')?.addEventListener('click', () => this.close(), { signal });
    this.input.addEventListener('input', this.#onInput, { signal });
    this.input.addEventListener('keydown', this.#onKeydown, { signal });
    this.clear?.addEventListener('click', () => {
      this.input.value = '';
      this.#onInput();
      this.input.focus();
    }, { signal });
    this.listbox.addEventListener('pointermove', this.#onPointerMove, { signal });
    this.panel.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
      }
    }, { signal });
    this.addEventListener('focusout', this.#onFocusOut, { signal });
    document.addEventListener('pointerdown', (event) => {
      if (this.isOpen && event.target instanceof Node && !this.panel.contains(event.target) && !this.toggle.contains(event.target)) {
        this.close();
      }
    }, { signal });
    // Capture phase on window so the shortcut wins over Horizon's search modal shortcut.
    window.addEventListener('keydown', this.#onGlobalKeydown, { signal, capture: true });
    window.addEventListener('resize', () => this.isOpen && this.#measure(), { signal, passive: true });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#request?.abort();
    window.clearTimeout(this.#debounce);
    window.clearTimeout(this.#hideTimer);
  }

  get isOpen() {
    return this.hasAttribute('data-open');
  }

  get #query() {
    return this.input.value.trim();
  }

  /** Place the bar flush under the header row the button sits in. */
  #measure() {
    const header = this.closest('header-component');
    const row = this.closest('.header__row') || header;
    if (!(row instanceof HTMLElement)) return;
    const rowBottom = row.getBoundingClientRect().bottom;
    // header-component uses `contain: layout`, so it is the containing block for fixed children.
    const headerTop = header instanceof HTMLElement ? header.getBoundingClientRect().top : 0;
    this.style.setProperty('--mc-search-top', `${Math.round(rowBottom - headerTop)}px`);
    this.style.setProperty('--mc-search-viewport-top', `${Math.round(rowBottom)}px`);
  }

  open() {
    if (this.isOpen) {
      this.input.focus();
      return;
    }
    window.clearTimeout(this.#hideTimer);
    this.#measure();
    this.panel.hidden = false;
    this.toggle.setAttribute('aria-expanded', 'true');
    this.#syncListbox();
    // Next frame so the reveal transition runs from the closed state.
    requestAnimationFrame(() => {
      this.setAttribute('data-open', '');
      this.input.focus({ preventScroll: true });
      if (this.input.value) this.input.select();
    });
  }

  /** @param {boolean} [returnFocus] */
  close(returnFocus = true) {
    if (!this.isOpen) return;
    this.removeAttribute('data-open');
    this.toggle.setAttribute('aria-expanded', 'false');
    this.input.setAttribute('aria-expanded', 'false');
    this.#setActive(-1);
    this.#hideTimer = window.setTimeout(() => {
      if (!this.isOpen) this.panel.hidden = true;
    }, CLOSE_MS);
    if (returnFocus) this.toggle.focus({ preventScroll: true });
  }

  /** @param {KeyboardEvent} event */
  #onGlobalKeydown = (event) => {
    if (!this.toggle.offsetParent) return;
    const target = event.target;
    const typing =
      target instanceof HTMLElement &&
      (target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName));
    const cmdK = event.key.toLowerCase() === 'k' && (event.metaKey || event.ctrlKey) && !event.altKey;
    const slash = event.key === '/' && !typing && !event.metaKey && !event.ctrlKey && !event.altKey;
    if (!cmdK && !slash) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.open();
  };

  /** @param {FocusEvent} event */
  #onFocusOut = (event) => {
    if (!this.isOpen) return;
    const next = event.relatedTarget;
    if (next instanceof Node && this.contains(next)) return;
    if (next === null) return; // Window blur or click on nothing focusable: keep it open.
    this.close(false);
  };

  #onInput = () => {
    const query = this.#query;
    if (this.clear) this.clear.hidden = query.length === 0;
    window.clearTimeout(this.#debounce);

    if (query.length === 0) {
      this.#request?.abort();
      this.#lastQuery = '';
      this.results.replaceChildren();
      this.results.hidden = true;
      delete this.dataset.loading;
      this.#syncListbox();
      return;
    }

    this.#debounce = window.setTimeout(() => this.#search(query), DEBOUNCE_MS);
  };

  /** @param {string} query */
  async #search(query) {
    if (query === this.#lastQuery && !this.results.hidden) return;
    this.#lastQuery = query;

    const cached = this.#cache.get(query);
    if (cached) {
      this.#render(cached);
      return;
    }

    this.#request?.abort();
    this.#request = new AbortController();
    this.dataset.loading = '';

    const url = new URL(this.dataset.suggestUrl || '/search/suggest', window.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('resources[type]', 'product,collection,query');
    url.searchParams.set('resources[limit]', '6');
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[options][prefix]', 'last');
    url.searchParams.set('section_id', SECTION_ID);

    try {
      const response = await fetch(url, { signal: this.#request.signal });
      if (!response.ok) throw new Error(String(response.status));
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const markup = doc.querySelector('[data-mc-sr]')?.outerHTML || '';
      this.#cache.set(query, markup);
      if (query === this.#query) this.#render(markup);
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;
      // Network trouble: Enter still submits to the full search page.
      this.results.hidden = true;
      this.#syncListbox();
    } finally {
      if (query === this.#query) delete this.dataset.loading;
    }
  }

  /** @param {string} markup */
  #render(markup) {
    this.results.innerHTML = markup;
    this.results.hidden = !markup;
    this.#syncListbox();
    const products = this.results.querySelectorAll('.mc-sr__product').length;
    if (this.status) {
      this.status.textContent = this.results.querySelector('.mc-sr__empty')
        ? 'No matching bottles'
        : `${products} bottle${products === 1 ? '' : 's'} found. Use the arrow keys to browse.`;
    }
  }

  /** Show popular searches for an empty field, results otherwise; refresh option ids. */
  #syncListbox() {
    const empty = this.#query.length === 0;
    if (this.popular instanceof HTMLElement) this.popular.hidden = !empty;
    const showing = empty ? this.popular instanceof HTMLElement : !this.results.hidden;
    this.listbox.hidden = !showing;
    this.input.setAttribute('aria-expanded', String(showing && this.isOpenOrOpening));
    this.#activeIndex = -1;
    this.input.removeAttribute('aria-activedescendant');
    for (const option of this.listbox.querySelectorAll('[role="option"]')) option.removeAttribute('id');
    this.#options().forEach((option, i) => {
      option.id = `McSearchOption-${i}`;
      option.setAttribute('tabindex', '-1');
      option.setAttribute('aria-selected', 'false');
    });
  }

  get isOpenOrOpening() {
    return !this.panel.hidden;
  }

  /** @returns {HTMLElement[]} */
  #options() {
    return Array.from(this.listbox.querySelectorAll('[role="option"]')).filter(
      (el) => el instanceof HTMLElement && !el.closest('[hidden]')
    );
  }

  /** @param {number} index */
  #setActive(index) {
    const options = this.#options();
    options.forEach((option, i) => option.setAttribute('aria-selected', String(i === index)));
    this.#activeIndex = index;
    const active = options[index];
    if (active) {
      this.input.setAttribute('aria-activedescendant', active.id || '');
      active.scrollIntoView({ block: 'nearest' });
    } else {
      this.input.removeAttribute('aria-activedescendant');
    }
  }

  /** @param {PointerEvent} event */
  #onPointerMove = (event) => {
    const option = event.target instanceof Element ? event.target.closest('[role="option"]') : null;
    if (!option) return;
    const index = this.#options().indexOf(/** @type {HTMLElement} */ (option));
    if (index !== this.#activeIndex) this.#setActive(index);
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    const options = this.#options();
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp': {
        if (!options.length) return;
        event.preventDefault();
        const delta = event.key === 'ArrowDown' ? 1 : -1;
        let next = this.#activeIndex + delta;
        if (next >= options.length) next = 0;
        if (next < -1) next = options.length - 1;
        this.#setActive(next);
        break;
      }
      case 'Enter': {
        const active = options[this.#activeIndex];
        if (active instanceof HTMLAnchorElement) {
          event.preventDefault();
          window.location.assign(active.href);
        }
        break;
      }
    }
  };
}

if (!customElements.get('mc-search-inline')) {
  customElements.define('mc-search-inline', McSearchInline);
}
